import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DocumentInfo, DocumentFact, DocumentDetail } from '../types';
import api from '../services/api';
import { useLiveReload } from './common/useLiveReload';
import { useWebSocket } from '../contexts/WebSocketContext';

interface DocumentsPanelProps {
  /** Which entity level this panel manages documents for */
  project_id?: number;
  category_id?: number;
  topic_id?: number;
  topic_keyword_id?: number;
  /** Label shown in the collapsible header */
  label?: string;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const FILE_TYPE_COLORS: Record<string, string> = {
  pdf: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  csv: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
  txt: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
};

export default function DocumentsPanel({
  project_id,
  category_id,
  topic_id,
  topic_keyword_id,
  label = 'Reference Documents',
}: DocumentsPanelProps) {
  const [documents, setDocuments] = useState<DocumentInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [addMode, setAddMode] = useState<'file' | 'paste'>('file');
  const [pasteTitle, setPasteTitle] = useState('');
  const [pasteContent, setPasteContent] = useState('');
  const [pasting, setPasting] = useState(false);

  const filters = { project_id, category_id, topic_id, topic_keyword_id };

  // Deep-link focus: page-builder match rows link here with
  // ?focus_doc=<id>[&focus_fact=<id>] so HIL can land on the bad fact.
  const [searchParams] = useSearchParams();
  const focusDoc = searchParams.get('focus_doc');
  const focusFact = searchParams.get('focus_fact');
  const focusDocId = focusDoc ? parseInt(focusDoc, 10) : null;
  const focusFactId = focusFact ? parseInt(focusFact, 10) : null;

  const fetchDocuments = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const docs = await api.getDocuments(filters);
      setDocuments(docs);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project_id, category_id, topic_id, topic_keyword_id]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);
  // A DOCUMENT FINISHING IN THE BACKGROUND changes its row (processed or
  // failed) or is new to the list (one made from a search result). Each row
  // only patched its fact count, so an error still read "N facts" and a new
  // document never joined. Re-read the list quietly when one finishes.
  useLiveReload((e) => e?.event_type === 'document_processing', () => void fetchDocuments(true));

  const handleUpload = async (file: File) => {
    setUploading(true);
    setError('');
    try {
      await api.uploadDocument(file, filters);
      await fetchDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePaste = async () => {
    if (!pasteContent.trim() || !pasteTitle.trim()) return;
    setPasting(true);
    setError('');
    try {
      await api.createDocumentFromText({
        content: pasteContent,
        title: pasteTitle,
        ...filters,
      });
      setPasteTitle('');
      setPasteContent('');
      await fetchDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create document');
    } finally {
      setPasting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.deleteDocument(id);
      setDocuments((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
        {documents.length > 0 && (
          <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
            {documents.length}
          </span>
        )}
      </div>

      <div className="space-y-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-3 py-2 rounded text-sm">
              {error}
              <button onClick={() => setError('')} className="ml-2 text-red-500 hover:text-red-700 dark:hover:text-red-300">&times;</button>
            </div>
          )}

          {/* Add document: file upload or paste */}
          <div>
            {/* A SOURCE PICKER, not two buttons. The old version gave the
                selected mode the same blue pill the native file input uses for
                its own "Choose File" button, so a choice and an action sat one
                above the other looking alike — and "Upload File" read as the
                verb that "Choose File" actually performs. Neutral segments in a
                tray, named after the thing rather than the act, leave exactly
                one blue control in the box: the one that does something. */}
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs text-gray-500 dark:text-gray-400">Add</span>
              <div
                role="radiogroup"
                aria-label="What to add"
                className="inline-flex p-0.5 rounded-md bg-gray-200/70 dark:bg-gray-700/70"
              >
                {(
                  [
                    ['file', 'a file'],
                    ['paste', 'pasted text'],
                  ] as ['file' | 'paste', string][]
                ).map(([mode, label]) => (
                  <button
                    key={mode}
                    role="radio"
                    aria-checked={addMode === mode}
                    onClick={() => setAddMode(mode)}
                    className={`px-2.5 py-1 text-xs font-medium rounded ${
                      addMode === mode
                        ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {addMode === 'file' ? (
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt,.pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleUpload(file);
                  }}
                  disabled={uploading}
                  className="flex-1 text-sm text-gray-500 dark:text-gray-400 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 dark:file:bg-blue-900/30 file:text-blue-700 dark:file:text-blue-300 hover:file:bg-blue-100 dark:hover:file:bg-blue-900/40 disabled:opacity-50"
                />
                {uploading && (
                  <span className="text-xs text-blue-600 dark:text-blue-400 animate-pulse">Uploading...</span>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <input
                  type="text"
                  value={pasteTitle}
                  onChange={(e) => setPasteTitle(e.target.value)}
                  placeholder="Document title"
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                <textarea
                  value={pasteContent}
                  onChange={(e) => setPasteContent(e.target.value)}
                  placeholder="Paste content here..."
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono"
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {pasteContent.trim() ? `${pasteContent.trim().split(/\s+/).length} words` : ''}
                  </span>
                  <button
                    onClick={handlePaste}
                    disabled={pasting || !pasteTitle.trim() || !pasteContent.trim()}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {pasting ? 'Creating...' : 'Create Document'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Documents list */}
          {loading ? (
            <div className="text-sm text-gray-400 dark:text-gray-500 animate-pulse">Loading...</div>
          ) : documents.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500 italic">No documents uploaded yet</p>
          ) : (
            <div className="space-y-2">
              {documents.map((doc) => (
                <DocumentRow
                  key={doc.id}
                  doc={doc}
                  onDelete={() => handleDelete(doc.id)}
                  focusFactId={focusDocId === doc.id ? focusFactId : null}
                  autoExpand={focusDocId === doc.id}
                  onFactCountChange={(delta) =>
                    setDocuments((prev) =>
                      prev.map((d) =>
                        d.id === doc.id ? { ...d, fact_count: Math.max(0, d.fact_count + delta) } : d
                      )
                    )
                  }
                />
              ))}
            </div>
          )}
        </div>
    </div>
  );
}

// ─── Document Row with expandable facts ──────────────────────────────────────

function DocumentRow({
  doc,
  onDelete,
  onFactCountChange,
  focusFactId,
  autoExpand,
}: {
  doc: DocumentInfo;
  onDelete: () => void;
  onFactCountChange: (delta: number) => void;
  focusFactId?: number | null;
  autoExpand?: boolean;
}) {
  const [expanded, setExpanded] = useState(!!autoExpand);
  const [view, setView] = useState<'facts' | 'source'>('facts');
  const [facts, setFacts] = useState<DocumentFact[] | null>(null);
  const [loadingFacts, setLoadingFacts] = useState(false);
  const [detail, setDetail] = useState<DocumentDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [flashFactId, setFlashFactId] = useState<number | null>(null);
  const { subscribe } = useWebSocket();

  // Once the focus props arrive, expand the row so its facts load and
  // scroll the document header into view.
  useEffect(() => {
    if (!autoExpand) return;
    setExpanded(true);
    const el = document.getElementById(`document-${doc.id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [autoExpand, doc.id]);

  // After facts load, scroll the focused fact into view + flash it.
  useEffect(() => {
    if (!focusFactId || !facts) return;
    if (!facts.some((f) => f.id === focusFactId)) return;
    const el = document.getElementById(`fact-${focusFactId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setFlashFactId(focusFactId);
      const t = setTimeout(() => setFlashFactId(null), 4000);
      return () => clearTimeout(t);
    }
  }, [focusFactId, facts]);

  const loadFacts = useCallback(async () => {
    setLoadingFacts(true);
    try {
      const data = await api.getDocumentFacts(doc.id);
      setFacts(data);
    } catch (err) {
      console.error('Failed to load facts:', err);
    } finally {
      setLoadingFacts(false);
    }
  }, [doc.id]);

  const loadDetail = useCallback(async () => {
    setLoadingDetail(true);
    try {
      const data = await api.getDocumentDetail(doc.id);
      setDetail(data);
    } catch (err) {
      console.error('Failed to load document source:', err);
    } finally {
      setLoadingDetail(false);
    }
  }, [doc.id]);

  useEffect(() => {
    if (!expanded) return;
    if (view === 'facts' && facts === null) loadFacts();
    if (view === 'source' && detail === null) loadDetail();
  }, [expanded, view, facts, detail, loadFacts, loadDetail]);

  // Listen for the background reprocess completion event
  useEffect(() => {
    return subscribe((event) => {
      if (event.event_type === 'document_processing' && event.document_id === doc.id) {
        setReprocessing(false);
        setFacts(null);
        onFactCountChange(event.fact_count);
      }
    });
  }, [subscribe, doc.id, onFactCountChange]);

  const handleReprocess = async () => {
    setReprocessing(true);
    try {
      // Returns immediately with fact_count=0 (facts were just deleted)
      const updated = await api.reprocessDocument(doc.id);
      setFacts(null);
      onFactCountChange(updated.fact_count - doc.fact_count);
    } catch (err) {
      console.error('Failed to reprocess document:', err);
      setReprocessing(false);
    }
    // reprocessing stays true until the WS push arrives
  };

  const handleDeleteFact = async (factId: number) => {
    try {
      await api.deleteDocumentFact(doc.id, factId);
      setFacts((prev) => prev?.filter((f) => f.id !== factId) ?? null);
      onFactCountChange(-1);
    } catch (err) {
      console.error('Failed to delete fact:', err);
    }
  };

  return (
    <div
      id={`document-${doc.id}`}
      className={`bg-white dark:bg-gray-800 border rounded-md overflow-hidden transition-colors ${
        autoExpand
          ? 'border-yellow-400 dark:border-yellow-700 ring-2 ring-yellow-300'
          : 'border-gray-200 dark:border-gray-700'
      }`}
    >
      {/* Document header */}
      <div className="flex items-center justify-between p-2.5">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex-1 min-w-0 flex items-start gap-2 text-left"
        >
          <svg
            className={`w-3.5 h-3.5 mt-0.5 text-gray-400 dark:text-gray-500 transition-transform flex-shrink-0 ${expanded ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {doc.filename}
              </span>
              <span
                className={`px-1.5 py-0.5 text-[10px] font-medium rounded uppercase ${
                  FILE_TYPE_COLORS[doc.file_type] || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}
              >
                {doc.file_type}
              </span>
              {doc.processing_state === 'processed' && (
                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                  {doc.fact_count} facts
                </span>
              )}
              {doc.processing_state === 'error' && (
                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">
                  Error
                </span>
              )}
              {doc.processing_state === 'pending' && (
                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300">
                  Pending
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              {formatBytes(doc.file_size_bytes)} &middot;{' '}
              {new Date(doc.created_at).toLocaleDateString()}
            </p>
          </div>
        </button>
        <button
          onClick={onDelete}
          className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-900/30 flex-shrink-0"
          title="Delete document"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>

      {/* Expanded panel */}
      {expanded && (
        <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          {/* Tab toggle */}
          <div className="flex gap-1 px-3 pt-2">
            <button
              onClick={() => setView('facts')}
              className={`px-2.5 py-1 text-xs font-medium rounded ${
                view === 'facts' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              Facts
            </button>
            <button
              onClick={() => setView('source')}
              className={`px-2.5 py-1 text-xs font-medium rounded ${
                view === 'source' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              Source
            </button>
          </div>

          <div className="px-3 py-2">
            {view === 'facts' ? (
              loadingFacts ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 animate-pulse py-1">Loading facts...</p>
              ) : !facts || facts.length === 0 ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 italic py-1">No facts</p>
              ) : (
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {facts.map((fact) => (
                    <div
                      key={fact.id}
                      id={`fact-${fact.id}`}
                      className={`flex items-start gap-2 group rounded transition-colors ${
                        flashFactId === fact.id
                          ? 'bg-yellow-100 dark:bg-yellow-900/40 ring-2 ring-yellow-300 p-1.5 -m-1.5'
                          : ''
                      }`}
                    >
                      <span className="text-[10px] text-gray-300 dark:text-gray-600 font-mono pt-1 flex-shrink-0 w-5 text-right">
                        {fact.chunk_index}
                      </span>
                      <p className="flex-1 text-xs text-gray-600 dark:text-gray-300 leading-relaxed line-clamp-3 min-w-0">
                        {fact.content}
                      </p>
                      <button
                        onClick={() => handleDeleteFact(fact.id)}
                        className="p-1 text-gray-300 dark:text-gray-600 hover:text-red-500 rounded hover:bg-red-50 dark:hover:bg-red-900/30 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove this fact"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )
            ) : (
              loadingDetail ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 animate-pulse py-1">Loading source...</p>
              ) : !detail ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 italic py-1">Source unavailable</p>
              ) : (
                <div className="space-y-2">
                  <pre className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap font-mono max-h-64 overflow-y-auto">
                    {detail.raw_content}
                  </pre>
                  <div className="flex justify-end">
                    <button
                      onClick={handleReprocess}
                      disabled={reprocessing}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {reprocessing ? 'Regenerating…' : 'Regenerate Facts'}
                    </button>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}
